import type {MutableRefObject} from "react";
import {useEffect, useRef} from "react";

import {isViewingAsUser} from "@/src/modules/core/lib/impersonation";
import type {Me} from "@/src/modules/core/types";

import {registerOrSyncPushSubscriptionSafe} from "../lib/push-subscription";

type PushSubscriptionSyncProps = {
  me: Me;
};

function syncPushForUser(
  me: Me,
  syncedUserIdRef: MutableRefObject<number | null>,
  options?: {force?: boolean},
) {
  if (isViewingAsUser(me)) {
    syncedUserIdRef.current = null;
    return;
  }

  if (!options?.force && syncedUserIdRef.current === me.user_id) {
    return;
  }

  void registerOrSyncPushSubscriptionSafe({captureFailures: !options?.force}).then(() => {
    syncedUserIdRef.current = me.user_id;
  });
}

/** Re-post browser push endpoint after login and on foreground when permission is granted. */
export function PushSubscriptionSync({me}: PushSubscriptionSyncProps) {
  const syncedUserIdRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (isViewingAsUser(me)) {
      syncedUserIdRef.current = null;
      return;
    }

    void registerOrSyncPushSubscriptionSafe().then(() => {
      if (!cancelled) {
        syncedUserIdRef.current = me.user_id;
      }
    });

    return () => {
      cancelled = true;
    };
  }, [me.user_id, me.impersonation?.active]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      syncPushForUser(me, syncedUserIdRef, {force: true});
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [me]);

  return null;
}
